import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import AiAssistantDrawer from 'domains/ai/components/AiAssistantDrawer';
import { consumeClientReviewDraft } from 'domains/ai/utils/storage';
import { useTranslation } from 'i18n/TranslationProvider';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import {
  UnifiedEmptyState,
  UnifiedLoadingState,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';
import {
  createListingReview,
  getReviewContext,
} from '../services/collaborationService';

const SENTIMENT_OPTIONS = ['positive', 'neutral', 'negative'];

export default function CreateReviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { listingId = '' } = useParams();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [stars, setStars] = useState(5);
  const [sentiment, setSentiment] = useState('positive');
  const [comment, setComment] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const nextContext = await getReviewContext(listingId);
        const preparedDraft = consumeClientReviewDraft(listingId);
        if (!cancelled) {
          setContext(nextContext);
          if (preparedDraft) {
            setStars(Number(preparedDraft.stars) || 5);
            setSentiment(
              SENTIMENT_OPTIONS.includes(preparedDraft.sentiment)
                ? preparedDraft.sentiment
                : 'positive',
            );
            setComment(preparedDraft.comment || '');
          }
        }
      } catch (error) {
        if (!cancelled) {
          setMessage(error.message || t('Failed to load review context.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [listingId, t]);

  const acceptedQuote = context?.acceptedQuote || null;

  const handleSubmit = async () => {
    if (!acceptedQuote?.id || !acceptedQuote?.partnerId) {
      return;
    }

    setSubmitting(true);
    try {
      await createListingReview(listingId, {
        quoteId: acceptedQuote.id,
        partnerId: acceptedQuote.partnerId,
        stars,
        sentiment,
        comment,
      });
      navigate(`/my-listings/${listingId}`);
    } catch (error) {
      setMessage(error.message || t('Review submission failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          title={context?.listing?.title || context?.listing?.humanId || t('Write Review')}
          description={
            context
              ? [context.listing?.route?.from || context.listing?.fromCity, context.listing?.route?.to || context.listing?.toCity]
                  .filter(Boolean)
                  .join(' → ')
              : t('Review the completed transport using the standard product flow.')
          }
          actions={
            <HStack spacing={3}>
              <Button size="sm" variant="outline" onClick={() => navigate(`/my-listings/${listingId}`)}>
                {t('Back to listing')}
              </Button>
              <AiAssistantDrawer
                role="client"
                screenContext={{
                  routeKey: 'client.reviews.create',
                  role: 'client',
                  surface: 'review-create',
                  entityType: 'listing',
                  entityId: listingId,
                  listingId,
                  listingTitle: context?.listing?.title || context?.listing?.humanId || '',
                }}
                buttonLabel={t('Draft with AI')}
                subtitle={t('Draft your review first, then submit it here when you are satisfied.')}
              />
            </HStack>
          }
        />

        {loading ? <UnifiedLoadingState label={t('Loading review details...')} minH="260px" /> : null}

        {!loading && !acceptedQuote ? (
          <UnifiedEmptyState
            title={t('Accepted quote required')}
            description={t('This listing needs an accepted quote before a review can be created.')}
            minH="220px"
          />
        ) : null}

        {!loading && acceptedQuote ? (
          <UnifiedSurfaceCard p={5}>
            <VStack align="stretch" spacing={5}>
              <Box>
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.16em" color="gray.500">
                  {t('Accepted quote')}
                </Text>
                <Text mt={1} fontWeight="800" fontSize="lg" color="navy.700" _dark={{ color: 'white' }}>
                  {acceptedQuote.partnerName || acceptedQuote.partnerId || t('Partner quote')}
                </Text>
                <Text mt={1} fontSize="sm" color="gray.500">
                  {t('Net price')}: {acceptedQuote.netPrice ?? 0} · {t('Gross price')}: {acceptedQuote.grossPrice ?? 0}
                </Text>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="700" color="gray.600" mb={2}>
                  {t('Stars')}
                </Text>
                <HStack spacing={2}>
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Button
                      key={value}
                      size="sm"
                      colorScheme={stars === value ? 'blue' : 'gray'}
                      variant={stars === value ? 'solid' : 'outline'}
                      onClick={() => setStars(value)}
                    >
                      {value}
                    </Button>
                  ))}
                </HStack>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="700" color="gray.600" mb={2}>
                  {t('Sentiment')}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                  {SENTIMENT_OPTIONS.map((option) => (
                    <Button
                      key={option}
                      size="sm"
                      colorScheme={sentiment === option ? 'blue' : 'gray'}
                      variant={sentiment === option ? 'solid' : 'outline'}
                      onClick={() => setSentiment(option)}
                    >
                      {t(option)}
                    </Button>
                  ))}
                </SimpleGrid>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="700" color="gray.600" mb={2}>
                  {t('Comment')}
                </Text>
                <Textarea
                  minH="160px"
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder={t('Share what went well and what could improve')}
                />
              </Box>

              {message ? (
                <Text fontSize="sm" color="red.500">
                  {message}
                </Text>
              ) : null}

              <HStack justify="flex-end">
                <Button colorScheme="blue" isLoading={submitting} onClick={handleSubmit}>
                  {submitting ? t('Submitting...') : t('Submit review')}
                </Button>
              </HStack>
            </VStack>
          </UnifiedSurfaceCard>
        ) : null}
      </VStack>
    </PageShell>
  );
}
